import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button, Card, Heading, Input } from '@openroad/ui';
import { safeNext } from '../lib/navigation';
import { useSession } from '../lib/session';

export function SignInScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { signIn, member } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const next = safeNext(params.get('next'));

  // Already signed in — nothing to do here. Rendering `<Navigate>` rather than
  // calling `navigate()` during render keeps the redirect a render result
  // instead of a side effect, which React would otherwise run twice in strict
  // mode and warn about.
  if (member) return <Navigate to={next} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate(next, { replace: true });
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'We could not sign you in. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto max-w-[440px] px-6 pt-14 pb-16">
      <Card radius="panel" className="p-8">
        <Heading level="panel" as="h1" className="mb-1.5">
          Welcome back
        </Heading>
        <p className="m-0 mb-6 text-base text-muted">
          Sign in to see your trips, message drivers and keep your saved CO₂ adding up.
        </p>

        <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          {error ? (
            <p
              role="alert"
              className="m-0 rounded-lg bg-pohutukawa-tint px-3.5 py-3 text-label text-danger"
            >
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="cta" size="hero" block disabled={submitting}>
            {submitting ? 'Signing you in…' : 'Sign in'}
          </Button>
        </form>

        <p className="mt-6 mb-0 text-meta text-subtle">
          New here?{' '}
          <Link to={`/sign-up?next=${encodeURIComponent(next)}`} className="text-ink underline">
            Create an account
          </Link>
          . Identity verification arrives in a later phase — a new account carries no badges.
        </p>
      </Card>
    </main>
  );
}
